/*
#*===================================================================
#*
#* Licensed Materials - Property of GameTimeFootage Inc.
#*
#* Copyright NextPro 2018. All Rights Reserved.
#*
#*===================================================================
*/

import { Injectable } from '@angular/core';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, defaultIfEmpty, map, switchMap } from 'rxjs/operators';
import { combineLatest, forkJoin, of } from 'rxjs';
import dayjs from 'dayjs';

import * as fromGetHighlightsAction from 'app/library/store/actions/get-library-highlights.action';
import { LibraryService } from 'app/library/services/library.service';
import { AssetsService } from 'app/shared/services/assets.service';
import { Constants } from 'app/shared/models/constants.model';

@Injectable()
export class GetLibraryHighlightsEffects {
  getHighlights$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromGetHighlightsAction.GET_LIBRARY_HIGHLIGHTS),
      switchMap(() => {
        const getHighlightReels$ = this.libraryService.getLibraryHighlightReels().pipe(
          switchMap((hrs) => {
            const augmentedHRRs = hrs.map((hr) => (hr.id ? this.augmentHRR(hr) : of(hr)));
            return forkJoin(augmentedHRRs).pipe(
              defaultIfEmpty([]),
              map((augmentedHrs: any) => {
                return augmentedHrs.map((hr, index) => {
                  if (hr.id) {
                    return {
                      ...hr,
                      draftIndex: index + 1,
                    };
                  } else {
                    return { ...hr, index: index + 1, type: this.libraryService.getHrType(hr.purchase.type) };
                  }
                });
              })
            );
          })
        );
        const getCustomVideos$ = this.libraryService.getLibraryHighlightsCustomVideos().pipe(
          map((videos) => {
            return videos
              .filter((a) => a.type === Constants.ASSET_TYPE.ATHLETE_CUSTOM_VIDEO)
              .map((a) => ({
                asset: a,
                active: true,
                custom: true,
                processing: a.type === Constants.ASSET_TYPE.ATHLETE_CUSTOM_VIDEO && !a.published,
              }));
          })
        );

        return combineLatest([getHighlightReels$, getCustomVideos$]).pipe(
          map((response) => {
            let data = response.reduce((acc, res) => {
              if (res.length) {
                acc = [...acc, ...res.filter((r) => !acc.find((a) => r.id && r.id === a.id))];
              }
              return acc;
            }, []);
            data = data
              .filter((hr) => !hr.purchase || (hr.purchase && hr.purchase.active))
              .map((hr) => {
                if (hr && hr.asset) {
                  if (hr.user) {
                    hr.asset.user = hr.user;
                  } else {
                    hr.asset.user = {
                      id: hr.asset.userId,
                      firstName: hr.asset.userFirstName,
                      lastName: hr.asset.userLastName,
                    };
                  }

                  hr.asset.urlThumb =
                    hr.asset.urlThumb || this.assetsService.getUrlThumb(hr.asset.url, hr.asset.is60fps);
                  hr.asset.uploadedAt =
                    hr.asset.metadata && hr.asset.metadata.uploadedAt
                      ? dayjs(hr.asset.metadata.uploadedAt).format('YYYY-MM-DD[T]HH:mm:ss.SSS')
                      : hr.asset.createdAt;
                }
                return hr;
              });
            return new fromGetHighlightsAction.GetLibraryHighlightsSuccess(data);
          }),
          catchError((error) => of(new fromGetHighlightsAction.GetLibraryHighlightsFail(error.message)))
        );
      })
    )
  );

  constructor(
    private actions$: Actions,
    private libraryService: LibraryService,
    private assetsService: AssetsService
  ) {}

  private augmentHRR(highlightReel) {
    return this.libraryService.getEstimatedDelivery(highlightReel).pipe(
      map((estimatedDeliveryAt) => {
        return {
          ...highlightReel,
          type: this.libraryService.getHrType(highlightReel.purchase.type),
          estimatedDeliveryAt,
        };
      })
    );
  }
}
